import { FormEvent, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createGuildInviteCode,
  createGuildManagedUploadCode,
  deleteAdminGuild,
  fetchGuildByServerSlug,
  fetchGuildInviteCodes,
  fetchGuildClassModeration,
  fetchGuildRoster,
  fetchGuildUploadCodes,
  leaveGuild,
  removeManagedGuildMember,
  revokeGuildInviteCode,
  revokeGuildManagedUploadCode,
  updateManagedGuild,
  upsertManagedGuildMember,
} from "../api/guilds";
import { updateCharacterClass } from "../api/characters";
import { EQ_CLASS_NAMES } from "../api/eqClasses";
import { fetchGuildReports } from "../api/reports";
import { fetchGuildProgressionByServerSlug } from "../api/progression";
import { useAuth } from "../auth/AuthContext";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import type {
  GuildClassModerationEntry,
  GuildInviteCodeSummary,
  GuildMemberSummary,
  GuildRosterResponse,
  GuildUploadCodeSummary,
  ProgressionBossRequirement,
  ProgressionExpansionSummary,
  ProgressionObjectiveSummary,
  ProgressionTierSummary,
} from "../api/types";
import {
  formatDateTime,
  formatDecimal,
  formatNumber,
} from "../utils/format";
import {
  getActiveServerSlug,
  serverCharacterPath,
  serverGuildPath,
  serverReportPath,
  serverReportsPath,
} from "../utils/siteContext";

const REPORTS_PER_PAGE = 50;
const GUILD_TABS = ["overview", "progression", "reports", "roster", "members"] as const;
type GuildTab = (typeof GUILD_TABS)[number];

function canManageGuild(role?: string | null) {
  return role === "OWNER" || role === "ADMIN" || role === "OFFICER";
}

function canEditGuildProfile(role?: string | null) {
  return role === "OWNER" || role === "OFFICER";
}

function isApplicationGuildManager(role?: string | null) {
  return role === "ADMINISTRATOR" || role === "SYSOP";
}

function classShortName(className: string | null | undefined) {
  if (!className) return "???";
  const shorthand: Record<string, string> = {
    bard: "BRD",
    beastlord: "BST",
    berserker: "BER",
    cleric: "CLR",
    druid: "DRU",
    enchanter: "ENC",
    magician: "MAG",
    monk: "MNK",
    necromancer: "NEC",
    paladin: "PAL",
    ranger: "RNG",
    rogue: "ROG",
    shadowknight: "SHD",
    "shadow knight": "SHD",
    shaman: "SHM",
    warrior: "WAR",
    wizard: "WIZ",
  };
  const normalized = className.trim().toLowerCase();
  return shorthand[normalized] ?? normalized.slice(0, 3).toUpperCase();
}

function classSourceLabel(source?: string | null) {
  if (!source) return "Unknown";
  return source.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type GuildProgressionData = {
  expansions?: ProgressionExpansionSummary[];
  objectives?: Array<ProgressionObjectiveSummary & {
    expansion?: ProgressionExpansionSummary;
    raidTier?: ProgressionTierSummary;
  }>;
};

function groupGuildObjectivesByExpansion(progression: GuildProgressionData): ProgressionExpansionSummary[] {
  if (Array.isArray(progression.expansions)) {
    return progression.expansions;
  }

  const objectives = progression.objectives ?? [];
  const expansionMap = new Map<string, ProgressionExpansionSummary>();

  for (const objective of objectives) {
    const objectiveExpansion = objective.expansion;
    const objectiveTier = objective.raidTier;

    const expansionId = objectiveExpansion?.id ?? "unknown-expansion";
    const tierId = objectiveTier?.id ?? "unknown-tier";

    let expansion = expansionMap.get(expansionId);
    if (!expansion) {
      expansion = {
        id: expansionId,
        slug: objectiveExpansion?.slug ?? "unknown",
        name: objectiveExpansion?.name ?? "Progression",
        sortOrder: objectiveExpansion?.sortOrder ?? 0,
        tiers: [],
      };
      expansionMap.set(expansionId, expansion);
    }

    const tiers = expansion.tiers ?? [];
    expansion.tiers = tiers;

    let tier = tiers.find((candidate) => candidate.id === tierId);
    if (!tier) {
      tier = {
        id: tierId,
        slug: objectiveTier?.slug ?? "unknown",
        name: objectiveTier?.name ?? "Objectives",
        sortOrder: objectiveTier?.sortOrder ?? 0,
        objectives: [],
      };
      tiers.push(tier);
    }

    const tierObjectives = tier.objectives ?? [];
    tier.objectives = tierObjectives;
    tierObjectives.push(objective);
  }

  return [...expansionMap.values()].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
}

function bossesFor(objective: ProgressionObjectiveSummary) {
  return objective.requiredBosses ?? objective.bosses ?? [];
}

function bossName(requirement: ProgressionBossRequirement) {
  return requirement.raidBoss?.name ?? requirement.boss?.name ?? requirement.name ?? "Unknown boss";
}

function bossZone(requirement: ProgressionBossRequirement) {
  return requirement.raidBoss?.raidZone?.name ?? requirement.boss?.raidZone?.name ?? requirement.zone ?? null;
}

function bossIsCompleted(requirement: ProgressionBossRequirement) {
  return Boolean(requirement.completed || requirement.killed || requirement.killedAt || requirement.firstKill);
}

function objectiveProgress(objective: ProgressionObjectiveSummary) {
  const bosses = bossesFor(objective);
  const completed = objective.completedCount ?? bosses.filter((boss) => bossIsCompleted(boss)).length;
  const required = objective.requiredBossCount ?? objective.requiredCount ?? bosses.length;
  return { completed, required };
}

function objectiveIsCompleted(objective: ProgressionObjectiveSummary, progress: { completed: number; required: number }, completionDate: string | null) {
  return Boolean(objective.isComplete || objective.isCompleted || completionDate || (progress.required > 0 && progress.completed >= progress.required));
}

function tabPath(basePath: string, tab: GuildTab) {
  return tab === "overview" ? basePath : `${basePath}/${tab}`;
}

function GuildTabs({ basePath, activeTab }: { basePath: string; activeTab: GuildTab }) {
  const tabs: Array<{ key: GuildTab; label: string }> = [
    { key: "overview", label: "Overview" },
    { key: "progression", label: "Progression" },
    { key: "reports", label: "Reports" },
    { key: "roster", label: "Roster" },
    { key: "members", label: "Members" },
  ];

  return (
    <section className="page-subnav">
      <div className="content-wrapper">
        <ul className="nav secondary-tabs">
          {tabs.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <Link className={`nav-link${activeTab === tab.key ? " active" : ""}`} to={tabPath(basePath, tab.key)}>
                {tab.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ReportsTable({
  reports,
  loading,
  page = 1,
  paginated = false,
  serverSlug,
}: {
  reports: Awaited<ReturnType<typeof fetchGuildReports>> | undefined;
  loading: boolean;
  page?: number;
  paginated?: boolean;
  serverSlug: string;
}) {
  const safeReports = reports ?? [];
  const start = paginated ? (page - 1) * REPORTS_PER_PAGE : 0;
  const visibleReports = paginated ? safeReports.slice(start, start + REPORTS_PER_PAGE) : safeReports.slice(0, 10);

  return (
    <div className="card rankings-card rankings-table-card bg-body-tertiary border-secondary-subtle shadow-sm">
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
            <thead>
              <tr>
                <th>Report</th>
                <th className="text-end">Encounters</th>
                <th className="text-end">POVs</th>
                <th>Last Upload</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-body-secondary py-3">Loading reports...</td></tr>
              ) : visibleReports.length > 0 ? (
                visibleReports.map((report) => (
                  <tr key={report.id}>
                    <td>
                      <Link className="link-light fw-semibold text-decoration-none" to={serverReportPath(serverSlug, report.id)}>
                        {report.displayName}
                      </Link>
                      <div className="small text-body-secondary">{formatDateTime(report.startedAt ?? report.firstUploadAt)}</div>
                    </td>
                    <td className="text-end tabular-nums">{report.encounterCount}</td>
                    <td className="text-end tabular-nums">{report.submissionCount}</td>
                    <td className="small text-body-secondary text-nowrap">{formatDateTime(report.lastUploadAt)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4} className="text-body-secondary py-3">No reports yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GuildProgressionSummary({ progression }: { progression: GuildProgressionData }) {
  const expansions = groupGuildObjectivesByExpansion(progression);

  return (
    <div className="d-grid gap-4">
      {expansions.length === 0 ? (
        <div className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm">
          <div className="card-body text-body-secondary">No progression objectives configured.</div>
        </div>
      ) : expansions.map((expansion) => {
        const tiers = tiersFor(expansion);
        return (
          <section className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm" key={expansion.id}>
            <div className="card-body">
              <h2 className="h5 mb-3">{expansion.name}</h2>
              <div className="d-grid gap-4">
                {tiers.map((tier) => {
                  const objectives = objectivesFor(tier);
                  return (
                    <div key={tier.id}>
                      <h3 className="h6 text-body-secondary mb-3">{tier.name}</h3>
                      {objectives.length === 0 ? (
                        <div className="text-body-secondary small">No objectives configured.</div>
                      ) : (
                        <div className="row g-3">
                          {objectives.map((objective) => {
                            const progress = objectiveProgress(objective);
                            const completionDate = objective.completedAt ?? objective.firstCompletedAt ?? objective.latestCompletedAt ?? null;
                            const isObjectiveCompleted = objectiveIsCompleted(objective, progress, completionDate);
                            const bosses = bossesFor(objective);
                            return (
                              <div className="col-12 col-xl-6" key={objective.id}>
                                <div className="border border-secondary-subtle p-3 h-100 bg-body">
                                  <div className="d-flex justify-content-between gap-3 mb-2">
                                    <div>
                                      <div className="fw-semibold">{objective.name}</div>
                                      <div className="small text-body-secondary">{progress.completed}/{progress.required} required bosses complete</div>
                                      {completionDate ? <div className="small text-success">Completed {formatDateTime(completionDate)}</div> : null}
                                    </div>
                                    <span className={isObjectiveCompleted ? "badge text-bg-success align-self-start" : "badge text-bg-dark align-self-start"}>
                                      {isObjectiveCompleted ? "Complete" : "Incomplete"}
                                    </span>
                                  </div>
                                  {bosses.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2">
                                      {bosses.map((boss, index) => {
                                        const zone = bossZone(boss);
                                        return (
                                          <span className={bossIsCompleted(boss) ? "badge text-bg-success border border-success" : "badge text-bg-dark border border-secondary-subtle"} title={zone ?? undefined} key={boss.raidBossId ?? boss.id ?? boss.raidBoss?.id ?? boss.boss?.id ?? `${objective.id}-${index}`}>
                                            {bossName(boss)}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function GuildRosterSummary({ roster, serverSlug }: { roster: GuildRosterResponse; serverSlug: string }) {
  const unknownCount = roster.classCounts.find((entry) => !entry.className)?.count ?? 0;

  return (
    <div className="guild-roster-layout">
      <div className="card rankings-card rankings-table-card bg-body-tertiary border-secondary-subtle shadow-sm guild-roster-class-card">
        <div className="card-body">
          <h2 className="h5 mb-3">Classes</h2>
          <div className="table-responsive mb-2">
            <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th className="text-end">Chars</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>All</td>
                  <td className="text-end tabular-nums">{formatNumber(roster.totalCharacters)}</td>
                </tr>
                {roster.classCounts.map((entry) => (
                  <tr key={entry.label}>
                    <td><span className="class-shorthand">{classShortName(entry.className)}</span></td>
                    <td className="text-end tabular-nums">{entry.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {unknownCount > 0 ? (
            <div className="small text-warning">{unknownCount} unknown</div>
          ) : null}
        </div>
      </div>

      <div className="card rankings-card rankings-table-card bg-body-tertiary border-secondary-subtle shadow-sm">
        <div className="card-body">
          <h2 className="h5 mb-3">Raid Roster</h2>
          <div className="table-responsive">
            <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Class</th>
                  <th>Source</th>
                  <th className="text-end">Best DPS</th>
                  <th className="text-end">Best HPS</th>
                  <th className="text-end">Parses</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {roster.characters.length === 0 ? (
                  <tr><td colSpan={7} className="text-body-secondary py-3">No active roster data yet.</td></tr>
                ) : roster.characters.map((row) => {
                  const character = row.character;
                  return (
                    <tr key={character.id}>
                      <td>
                        <Link className="link-light fw-semibold text-decoration-none" to={serverCharacterPath(serverSlug, character)}>
                          {character.name}
                        </Link>
                      </td>
                      <td><span className="class-shorthand">{classShortName(character.className)}</span></td>
                      <td className="small text-body-secondary">
                        {classSourceLabel(character.classSource)}
                        {typeof character.classConfidence === "number" ? ` · ${Math.round(character.classConfidence * 100)}%` : ""}
                      </td>
                      <td className="text-end tabular-nums">{row.bestDamage ? formatDecimal(row.bestDamage.perSecond, 1) : <span className="text-body-secondary">-</span>}</td>
                      <td className="text-end tabular-nums">{row.bestHealing ? formatDecimal(row.bestHealing.perSecond, 1) : <span className="text-body-secondary">-</span>}</td>
                      <td className="text-end tabular-nums">{row.parseCount}</td>
                      <td className="small text-body-secondary text-nowrap">{formatDateTime(row.lastSeenAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function GuildClassModerationPanel({
  queue,
  isLoading,
  error,
  onApprove,
  onOverride,
  isPending,
}: {
  queue: GuildClassModerationEntry[];
  isLoading: boolean;
  error: Error | null;
  onApprove: (characterId: string, className: string) => void;
  onOverride: (characterId: string, className: string | null) => void;
  isPending: boolean;
}) {
  const [overrides, setOverrides] = useState<Record<string, string>>(Object.create(null));

  function selectedOverride(entry: GuildClassModerationEntry) {
    return overrides[entry.character.id] ?? entry.suggestedClass ?? "";
  }

  return (
    <section>
      <div className="card rankings-card rankings-table-card bg-body-tertiary border-secondary-subtle shadow-sm">
        <div className="card-body">
          <h2 className="h5 mb-3">Class Moderation</h2>
          {isLoading ? <LoadingState label="Loading class moderation queue" /> : null}
          {error ? <ErrorState title="Could not load class moderation queue" error={error} /> : null}
          {!isLoading && !error ? (
            <div className="table-responsive">
              <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
                <thead>
                  <tr>
                    <th>Character</th>
                    <th>Current</th>
                    <th>Suggested</th>
                    <th>Confidence</th>
                    <th>Evidence</th>
                    <th>Last Seen</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.length === 0 ? (
                    <tr><td colSpan={7} className="text-body-secondary py-3">No unknown or auto-detected characters need review right now.</td></tr>
                  ) : queue.map((entry) => {
                    const selected = selectedOverride(entry);
                    return (
                      <tr key={entry.character.id}>
                        <td className="fw-semibold">{entry.character.name}</td>
                        <td><span className="class-shorthand">{classShortName(entry.character.className)}</span></td>
                        <td>{entry.suggestedClass ? <span className="class-shorthand">{classShortName(entry.suggestedClass)}</span> : <span className="text-body-secondary">None</span>}</td>
                        <td>
                          <span className={entry.confidenceLabel === "High" ? "badge text-bg-success" : entry.confidenceLabel === "Medium" ? "badge text-bg-warning text-dark" : "badge text-bg-secondary"}>
                            {entry.confidenceLabel}
                            {typeof entry.suggestedConfidence === "number" ? ` · ${Math.round(entry.suggestedConfidence * 100)}%` : ""}
                          </span>
                        </td>
                        <td className="small text-body-secondary">
                          {entry.evidenceSummary.length > 0 ? (
                            <ul className="list-unstyled mb-0">
                              {entry.evidenceSummary.map((summary) => <li key={summary}>{summary}</li>)}
                            </ul>
                          ) : "No evidence collected"}
                        </td>
                        <td className="small text-body-secondary text-nowrap">{entry.lastSeenAt ? formatDateTime(entry.lastSeenAt) : "-"}</td>
                        <td>
                          <div className="d-flex justify-content-end gap-2">
                            {entry.suggestedClass ? (
                              <button className="btn btn-sm btn-outline-success" type="button" disabled={isPending} onClick={() => onApprove(entry.character.id, entry.suggestedClass!)}>
                                Approve
                              </button>
                            ) : null}
                            <select className="form-select form-select-sm w-auto" value={selected} disabled={isPending} onChange={(event) => setOverrides((current) => ({ ...current, [entry.character.id]: event.target.value }))}>
                              <option value="">Unknown</option>
                              {EQ_CLASS_NAMES.map((className) => <option value={className} key={className}>{className}</option>)}
                            </select>
                            <button className="btn btn-sm btn-outline-light" type="button" disabled={isPending} onClick={() => onOverride(entry.character.id, selected || null)}>
                              Save
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function MembersTable({ members }: { members: GuildMemberSummary[] }) {
  return (
    <div className="card rankings-card rankings-table-card bg-body-tertiary border-secondary-subtle shadow-sm">
      <div className="card-body">
        <h2 className="h5 mb-3">Members</h2>
        <div className="table-responsive">
          <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Guild Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan={3} className="text-body-secondary py-3">No members yet.</td></tr>
              ) : members.map((member) => (
                <tr key={member.id}>
                  <td>{member.user?.username ?? "Unknown user"}</td>
                  <td>{member.role}</td>
                  <td className="small text-body-secondary">{formatDateTime(member.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GuildSettingsPanel({
  guild,
  canManage,
  pending,
  error,
  onSave,
}: {
  guild: { name: string; slug: string; description: string | null };
  canManage: boolean;
  pending: boolean;
  error?: Error | null;
  onSave: (input: { name: string; slug: string; description: string }) => void;
}) {
  const [name, setName] = useState(guild.name);
  const [slug, setSlug] = useState(guild.slug);
  const [description, setDescription] = useState(guild.description ?? "");
  return (
    <section className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm">
      <div className="card-body">
        <h2 className="h5 mb-3">Guild Settings</h2>
        <form className="d-grid gap-3" onSubmit={(event) => { event.preventDefault(); onSave({ name, slug, description }); }}>
          <input className="form-control" value={name} onChange={(event) => setName(event.target.value)} placeholder="Guild name" disabled={!canManage || pending} />
          <input className="form-control" value={slug} onChange={(event) => setSlug(event.target.value)} placeholder="Slug" disabled={!canManage || pending} />
          <textarea className="form-control" rows={3} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Description" disabled={!canManage || pending} />
          <div className="d-flex justify-content-between align-items-center">
            {!canManage ? <div className="small text-body-secondary">Only owners, admins, and officers can edit guild details.</div> : <span />}
            <button className="btn btn-outline-light" type="submit" disabled={!canManage || pending}>Save Guild Details</button>
          </div>
          {error ? <div className="text-danger small">{error.message}</div> : null}
        </form>
      </div>
    </section>
  );
}

function CodeList({
  title,
  description,
  codes,
  loading,
  newCode,
  label,
  placeholder,
  canManage,
  pendingCreate,
  createError,
  revokeError,
  onLabelChange,
  onCreate,
  onCopy,
  onRevoke,
}: {
  title: string;
  description: string;
  codes: Array<GuildUploadCodeSummary | GuildInviteCodeSummary> | undefined;
  loading: boolean;
  newCode: string | null;
  label: string;
  placeholder: string;
  canManage: boolean;
  pendingCreate: boolean;
  createError?: Error | null;
  revokeError?: Error | null;
  onLabelChange: (value: string) => void;
  onCreate: (event: FormEvent) => void;
  onCopy: (value: string) => void;
  onRevoke: (id: string) => void;
}) {
  return (
    <section className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm">
      <div className="card-body">
        <h2 className="h5 mb-3">{title}</h2>
        <p className="small text-body-secondary">{description}</p>
        {newCode ? (
          <div className="alert alert-success">
            <div className="small text-uppercase fw-semibold">New code</div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <code>{newCode}</code>
              <button className="btn btn-sm btn-outline-success" type="button" onClick={() => onCopy(newCode)}>Copy</button>
            </div>
          </div>
        ) : null}
        {canManage ? (
          <form className="d-flex gap-2 mb-3" onSubmit={onCreate}>
            <input className="form-control" placeholder={placeholder} value={label} onChange={(event) => onLabelChange(event.target.value)} />
            <button className="btn btn-primary text-nowrap" type="submit" disabled={pendingCreate}>Generate</button>
          </form>
        ) : null}
        {createError ? <div className="text-danger small mb-2">{createError.message}</div> : null}
        {revokeError ? <div className="text-danger small mb-2">{revokeError.message}</div> : null}
        {loading ? <LoadingState label={`Loading ${title.toLowerCase()}`} /> : null}
        {codes ? (
          <div className="list-group list-group-flush border border-secondary-subtle overflow-hidden">
            {codes.length === 0 ? (
              <div className="list-group-item bg-body text-body-secondary">No codes.</div>
            ) : codes.map((code) => (
              <div key={code.id} className="list-group-item bg-body text-light d-flex justify-content-between align-items-center gap-2">
                <div>
                  <div className="fw-semibold">{code.label || title.replace(/s$/, "")}</div>
                  <div className="small text-body-secondary">
                    Created {formatDateTime(code.createdAt)} · {code.isActive ? "Active" : "Inactive"}
                    {code.lastUsedAt ? ` · Last used ${formatDateTime(code.lastUsedAt)}` : ""}
                  </div>
                  {code.code ? (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      <code>{code.code}</code>
                      <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => onCopy(code.code ?? "")}>Copy</button>
                    </div>
                  ) : null}
                </div>
                {canManage && code.isActive ? (
                  <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => onRevoke(code.id)}>Revoke</button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function GuildDetailPage() {
  const { guildSlug = "", guildTab } = useParams<{ guildSlug: string; guildTab?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeServerSlug = getActiveServerSlug();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["guild", activeServerSlug, guildSlug],
    queryFn: () => fetchGuildByServerSlug(activeServerSlug, guildSlug),
    enabled: Boolean(activeServerSlug && guildSlug),
  });
  const [inviteLabel, setInviteLabel] = useState("");
  const [newInviteCode, setNewInviteCode] = useState<string | null>(null);
  const [uploadCodeLabel, setUploadCodeLabel] = useState("");
  const [newUploadCode, setNewUploadCode] = useState<string | null>(null);

  const activeTab = GUILD_TABS.includes(guildTab as GuildTab) ? (guildTab as GuildTab) : "overview";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const myMembership = useMemo(() => {
    if (!auth.user || !query.data) return null;
    return query.data.members.find((member) => member.user?.id === auth.user?.id) ?? null;
  }, [auth.user, query.data]);

  const member = Boolean(myMembership);
  const manager = canManageGuild(myMembership?.role) || isApplicationGuildManager(auth.user?.role);
  const guildProfileEditor = canEditGuildProfile(myMembership?.role) || isApplicationGuildManager(auth.user?.role);
  const appAdminManager = isApplicationGuildManager(auth.user?.role);

  const uploadCodesQuery = useQuery({
    queryKey: ["guild", query.data?.guild.id, "uploadCodes"],
    queryFn: () => fetchGuildUploadCodes(query.data!.guild.id),
    enabled: Boolean(query.data?.guild.id && member),
  });

  const inviteCodesQuery = useQuery({
    queryKey: ["guild", query.data?.guild.id, "inviteCodes"],
    queryFn: () => fetchGuildInviteCodes(query.data!.guild.id),
    enabled: Boolean(query.data?.guild.id && member),
  });

  const guildReportsQuery = useQuery({
    queryKey: ["guild", query.data?.guild.id, "reports", activeServerSlug],
    queryFn: () => fetchGuildReports(query.data!.guild.id, { limit: 500, serverSlug: activeServerSlug }),
    enabled: Boolean(query.data?.guild.id),
  });

  const guildRosterQuery = useQuery({
    queryKey: ["guild", query.data?.guild.id, "roster", 30],
    queryFn: () => fetchGuildRoster(query.data!.guild.id, { days: 30, limit: 500 }),
    enabled: Boolean(query.data?.guild.id),
  });

  const classModerationQuery = useQuery({
    queryKey: ["guild", query.data?.guild.id, "classModeration"],
    queryFn: () => fetchGuildClassModeration(query.data!.guild.id),
    enabled: Boolean(query.data?.guild.id && manager),
  });

  const progressionQuery = useQuery({
    queryKey: ["guild-progression", activeServerSlug, guildSlug],
    queryFn: () => fetchGuildProgressionByServerSlug(activeServerSlug, guildSlug),
    enabled: Boolean(activeServerSlug && guildSlug && query.data?.guild.id),
  });

  const createUploadCodeMutation = useMutation({
    mutationFn: () => createGuildManagedUploadCode(query.data!.guild.id, { label: uploadCodeLabel || undefined }),
    onSuccess: async (uploadCode) => {
      setUploadCodeLabel("");
      setNewUploadCode(uploadCode.code ?? null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "uploadCodes"] }),
        queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] }),
      ]);
    },
  });

  const revokeUploadCodeMutation = useMutation({
    mutationFn: (codeId: string) => revokeGuildManagedUploadCode(query.data!.guild.id, codeId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "uploadCodes"] }),
        queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] }),
      ]);
    },
  });

  const createInviteMutation = useMutation({
    mutationFn: () => createGuildInviteCode(query.data!.guild.id, { label: inviteLabel || undefined }),
    onSuccess: async (inviteCode) => {
      setInviteLabel("");
      setNewInviteCode(inviteCode.code ?? null);
      await queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "inviteCodes"] });
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (codeId: string) => revokeGuildInviteCode(query.data!.guild.id, codeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "inviteCodes"] });
    },
  });

  const leaveMutation = useMutation({
    mutationFn: () => leaveGuild(query.data!.guild.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] }),
        queryClient.invalidateQueries({ queryKey: ["me", "guilds"] }),
        queryClient.invalidateQueries({ queryKey: ["guilds"] }),
      ]);
    },
  });

  const updateGuildMutation = useMutation({
    mutationFn: (input: { name: string; slug: string; description: string }) => updateManagedGuild(query.data!.guild.id, input),
    onSuccess: async (updatedGuild) => {
      await queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] });
      const resolvedCurrentSlug = String(guildSlug ?? "").trim().toLowerCase();
      const resolvedNextSlug = String(updatedGuild.slug ?? "").trim().toLowerCase();
      if (resolvedNextSlug && resolvedNextSlug !== resolvedCurrentSlug) {
        navigate(serverGuildPath(activeServerSlug, updatedGuild), { replace: true });
      }
    },
  });

  const deleteGuildMutation = useMutation({
    mutationFn: () => deleteAdminGuild(query.data!.guild.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["guilds"] }),
        queryClient.invalidateQueries({ queryKey: ["me", "guilds"] }),
      ]);
      navigate("/guilds");
    },
  });

  const upsertMemberMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => upsertManagedGuildMember(query.data!.guild.id, { userId, role }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => removeManagedGuildMember(query.data!.guild.id, memberId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["guild", activeServerSlug, guildSlug] });
    },
  });

  const updateClassMutation = useMutation({
    mutationFn: ({ characterId, className }: { characterId: string; className: string | null }) => updateCharacterClass(characterId, className),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "classModeration"] }),
        queryClient.invalidateQueries({ queryKey: ["guild", query.data?.guild.id, "roster", 30] }),
      ]);
    },
  });

  function handleCreateUploadCode(event: FormEvent) {
    event.preventDefault();
    setNewUploadCode(null);
    createUploadCodeMutation.mutate();
  }

  async function copyText(value: string) {
    await navigator.clipboard.writeText(value);
  }

  function handleCreateInvite(event: FormEvent) {
    event.preventDefault();
    setNewInviteCode(null);
    createInviteMutation.mutate();
  }

  if (!guildSlug) {
    return <div className="content-wrapper page-main"><ErrorState title="Invalid guild" error={new Error("Missing guild slug")} /></div>;
  }
  if (guildTab && !GUILD_TABS.includes(guildTab as GuildTab)) {
    return <Navigate to={`/guilds/${guildSlug}`} replace />;
  }
  if (query.isLoading) return <LoadingState label="Loading guild" />;
  if (query.isError) {
    return <div className="content-wrapper page-main"><ErrorState title="Could not load guild" error={query.error} /></div>;
  }
  if (!query.data) return null;

  const { guild, members } = query.data;
  const basePath = serverGuildPath(activeServerSlug, guild);
  const reportCount = guildReportsQuery.data?.length ?? 0;
  const totalReportPages = Math.max(1, Math.ceil(reportCount / REPORTS_PER_PAGE));

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-5">
          <h1 className="display-6 fw-semibold mb-0">{guild.name}</h1>
          <span className="page-hero__server d-block">{guild.server?.name ?? "Unknown server"}</span>
        </div>
      </section>

      <GuildTabs basePath={basePath} activeTab={activeTab} />

      <div className="content-wrapper page-main">
        {myMembership ? (
          <div className="alert alert-info border-info-subtle d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>You are a <strong>{myMembership.role}</strong> of this guild.</div>
            <button className="btn btn-sm btn-outline-light" type="button" onClick={() => leaveMutation.mutate()} disabled={leaveMutation.isPending}>Leave Guild</button>
          </div>
        ) : null}
        {leaveMutation.isError ? <div className="alert alert-danger">{leaveMutation.error.message}</div> : null}

        {activeTab === "overview" ? (
          <div className="d-grid gap-4">
            <section className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm">
              <div className="card-body">
                <h2 className="h5 mb-3">Overview</h2>
                <p className="mb-0 text-body-secondary">{guild.description || "No guild description has been added yet."}</p>
              </div>
            </section>
            {guildProfileEditor ? (
              <GuildSettingsPanel
                guild={guild}
                canManage={guildProfileEditor}
                pending={updateGuildMutation.isPending}
                error={updateGuildMutation.error}
                onSave={(input) => updateGuildMutation.mutate(input)}
              />
            ) : null}
            {appAdminManager ? (
              <section className="card rankings-card bg-body-tertiary border-danger-subtle shadow-sm">
                <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <h2 className="h5 mb-1 text-danger">Delete Guild</h2>
                    <div className="small text-body-secondary">Permanently delete this guild (sysop/administrator only).</div>
                  </div>
                  <button
                    className="btn btn-outline-danger"
                    type="button"
                    disabled={deleteGuildMutation.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete guild "${guild.name}"? This action cannot be undone.`)) {
                        deleteGuildMutation.mutate();
                      }
                    }}
                  >
                    Delete Guild
                  </button>
                </div>
                {deleteGuildMutation.isError ? <div className="card-footer text-danger small">{deleteGuildMutation.error.message}</div> : null}
              </section>
            ) : null}

            {member ? (
              <div className="row g-4">
                <div className="col-12 col-xl-6">
                  <CodeList
                    title="fzClient Upload Codes"
                    description="Reusable codes that credit fzClient uploads to this guild."
                    codes={uploadCodesQuery.data}
                    loading={uploadCodesQuery.isLoading}
                    newCode={newUploadCode}
                    label={uploadCodeLabel}
                    placeholder="Label, optional"
                    canManage={manager}
                    pendingCreate={createUploadCodeMutation.isPending}
                    createError={createUploadCodeMutation.error}
                    revokeError={revokeUploadCodeMutation.error}
                    onLabelChange={setUploadCodeLabel}
                    onCreate={handleCreateUploadCode}
                    onCopy={(value) => void copyText(value)}
                    onRevoke={(id) => revokeUploadCodeMutation.mutate(id)}
                  />
                </div>
                <div className="col-12 col-xl-6">
                  <CodeList
                    title="Member Invite Codes"
                    description="Invite codes let logged-in users join this guild."
                    codes={inviteCodesQuery.data}
                    loading={inviteCodesQuery.isLoading}
                    newCode={newInviteCode}
                    label={inviteLabel}
                    placeholder="Label, optional"
                    canManage={manager}
                    pendingCreate={createInviteMutation.isPending}
                    createError={createInviteMutation.error}
                    onLabelChange={setInviteLabel}
                    onCreate={handleCreateInvite}
                    onCopy={(value) => void copyText(value)}
                    onRevoke={(id) => revokeInviteMutation.mutate(id)}
                  />
                </div>
              </div>
            ) : null}

            <section>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h2 className="h5 mb-0">Recent Reports</h2>
                <Link className="btn btn-outline-light btn-sm" to={serverReportsPath(activeServerSlug)}>All Server Reports</Link>
              </div>
              <ReportsTable reports={guildReportsQuery.data} loading={guildReportsQuery.isLoading} serverSlug={activeServerSlug} />
            </section>
          </div>
        ) : null}

        {activeTab === "progression" ? (
          <>
            {progressionQuery.isLoading ? <LoadingState label="Loading guild progression" /> : null}
            {progressionQuery.isError ? <ErrorState title="Could not load guild progression" error={progressionQuery.error} /> : null}
            {progressionQuery.data ? <GuildProgressionSummary progression={progressionQuery.data} /> : null}
          </>
        ) : null}

        {activeTab === "reports" ? (
          <section>
            <h2 className="h5 mb-3">Reports</h2>
            <ReportsTable reports={guildReportsQuery.data} loading={guildReportsQuery.isLoading} page={page} paginated serverSlug={activeServerSlug} />
            {totalReportPages > 1 ? (
              <nav className="d-flex justify-content-center gap-2 mt-4" aria-label="Guild reports pagination">
                <button className="btn btn-outline-light btn-sm" disabled={page <= 1} onClick={() => setSearchParams(page - 1 <= 1 ? {} : { page: String(page - 1) })}>Previous</button>
                <span className="align-self-center small text-body-secondary">Page {page} of {totalReportPages}</span>
                <button className="btn btn-outline-light btn-sm" disabled={page >= totalReportPages} onClick={() => setSearchParams({ page: String(page + 1) })}>Next</button>
              </nav>
            ) : null}
          </section>
        ) : null}

        {activeTab === "roster" ? (
          <div className="d-grid gap-4">
            {guildRosterQuery.isLoading ? <LoadingState label="Loading guild roster" /> : null}
            {guildRosterQuery.isError ? <ErrorState title="Could not load guild roster" error={guildRosterQuery.error} /> : null}
            {guildRosterQuery.data ? <GuildRosterSummary roster={guildRosterQuery.data} serverSlug={activeServerSlug} /> : null}
            {manager ? (
              <GuildClassModerationPanel
                queue={classModerationQuery.data?.queue ?? []}
                isLoading={classModerationQuery.isLoading}
                error={classModerationQuery.isError ? classModerationQuery.error : null}
                isPending={updateClassMutation.isPending}
                onApprove={(characterId, className) => updateClassMutation.mutate({ characterId, className })}
                onOverride={(characterId, className) => updateClassMutation.mutate({ characterId, className })}
              />
            ) : null}
            {updateClassMutation.isError ? <div className="alert alert-danger">{updateClassMutation.error.message}</div> : null}
          </div>
        ) : null}

        {activeTab === "members" ? (
          <div className="d-grid gap-3">
            <MembersTable members={members} />
            {manager ? (
              <section className="card rankings-card bg-body-tertiary border-secondary-subtle shadow-sm">
                <div className="card-body">
                  <h2 className="h5 mb-3">Manage Membership</h2>
                  <form className="d-flex flex-wrap gap-2 mb-3" onSubmit={(event) => {
                    event.preventDefault();
                    const form = new FormData(event.currentTarget);
                    const userId = String(form.get("userId") ?? "").trim();
                    const role = String(form.get("role") ?? "MEMBER");
                    if (userId) upsertMemberMutation.mutate({ userId, role });
                  }}>
                    <input className="form-control" style={{ maxWidth: 280 }} name="userId" placeholder="User ID" />
                    <select className="form-select" style={{ maxWidth: 180 }} name="role" defaultValue="MEMBER">
                      <option value="MEMBER">MEMBER</option><option value="OFFICER">OFFICER</option><option value="ADMIN">ADMIN</option><option value="OWNER">OWNER</option>
                    </select>
                    <button className="btn btn-primary" type="submit" disabled={upsertMemberMutation.isPending}>Add or Update</button>
                  </form>
                  {(upsertMemberMutation.error || removeMemberMutation.error) ? <div className="text-danger small mb-2">{upsertMemberMutation.error?.message ?? removeMemberMutation.error?.message}</div> : null}
                  <div className="list-group">
                    {members.map((memberItem) => (
                      <div className="list-group-item bg-body text-light d-flex justify-content-between align-items-center" key={`${memberItem.id}-manage`}>
                        <div>{memberItem.user?.username ?? "Unknown user"} <span className="small text-body-secondary">({memberItem.role})</span></div>
                        <button className="btn btn-sm btn-outline-danger" type="button" disabled={removeMemberMutation.isPending} onClick={() => removeMemberMutation.mutate(memberItem.id)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
